import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Compass, UserSquare2, FolderTree, Users as UsersIcon, CreditCard, TrendingUp, FileText, Library, ExternalLink, LogOut, Home as HomeIcon } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_e31510f3-0985-434c-b5ba-05b91fac6a7c/artifacts/ywyzdsaz_logomarca.jpeg";

export default function AdminLayout({ children, title, eyebrow, actions }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const nav = [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, test: "admin-nav-dashboard" },
        { to: "/admin/home", label: "Home", icon: HomeIcon, test: "admin-nav-home" },
        { to: "/admin/jornadas", label: "Jornadas", icon: Compass, test: "admin-nav-journeys" },
        { to: "/admin/mentores", label: "Mentores", icon: UserSquare2, test: "admin-nav-mentors" },
        { to: "/admin/categorias", label: "Categorias", icon: FolderTree, test: "admin-nav-categories" },
        { to: "/admin/biblioteca", label: "Biblioteca", icon: Library, test: "admin-nav-library" },
        { to: "/admin/planos", label: "Planos & Upsell", icon: CreditCard, test: "admin-nav-plans" },
        { to: "/admin/receita", label: "Receita", icon: TrendingUp, test: "admin-nav-revenue" },
        { to: "/admin/paginas", label: "Páginas", icon: FileText, test: "admin-nav-pages" },
        { to: "/admin/usuarios", label: "Usuários", icon: UsersIcon, test: "admin-nav-users" },
    ];

    const linkCls = ({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl font-display text-sm transition-colors ${
            isActive ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-black/5"
        }`;

    return (
        <div className="min-h-screen bg-[#F8F8F5]">
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
                {/* Sidebar */}
                <aside data-testid="admin-sidebar" className="border-r border-black/5 bg-white lg:sticky lg:top-0 lg:h-screen flex flex-col">
                    <div className="px-5 py-6 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl overflow-hidden bg-black">
                            <img src={LOGO} alt="IMQ" className="w-full h-full object-cover" />
                        </div>
                        <div className="leading-tight">
                            <div className="overline text-[#5C5C5C]">Painel</div>
                            <div className="font-display font-semibold tracking-tight">Admin IMQ</div>
                        </div>
                    </div>

                    <nav className="px-3 py-2 space-y-1 flex-1">
                        {nav.map(({ to, label, icon: Icon, end, test }) => (
                            <NavLink key={to} to={to} end={end} className={linkCls} data-testid={test}>
                                <Icon className="w-4 h-4" /> {label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="p-3 border-t border-black/5 space-y-2">
                        <Link to="/" data-testid="admin-back-site" className="flex items-center gap-3 px-4 py-2.5 rounded-xl font-display text-sm text-[#5C5C5C] hover:text-[#1A1A1A] hover:bg-black/5">
                            <ExternalLink className="w-4 h-4" /> Voltar ao site
                        </Link>
                        <button
                            onClick={async () => { await logout(); navigate("/"); }}
                            data-testid="admin-logout"
                            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl font-display text-sm text-[#5C5C5C] hover:text-red-600 hover:bg-red-50"
                        >
                            <LogOut className="w-4 h-4" /> Sair · {user?.name?.split(" ")[0]}
                        </button>
                    </div>
                </aside>

                {/* Content */}
                <main className="px-6 md:px-10 py-10 max-w-[1400px] w-full">
                    <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
                        <div>
                            {eyebrow && <div className="overline text-[#5C5C5C]">{eyebrow}</div>}
                            <h1 className="font-display font-light text-4xl md:text-5xl tracking-tight mt-1">{title}</h1>
                        </div>
                        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
                    </header>
                    {children}
                </main>
            </div>
        </div>
    );
}
